/**
 * Quantity formatting shared by the servings calculator and the weekly menu —
 * mirrors Smakota\Core\Recipe::format_qty() / unit_label().
 */

// Per-language grammar from Smakota\Core\Languages (window.winonweb.grammar);
// the fallbacks keep the calculator right even without it.
const FALLBACK = {
	uk: { plural: 'slavic', decimal: ',' },
	en: { plural: 'en', decimal: '.' },
};
function grammar( lang ) {
	const g = ( window.winonweb && window.winonweb.grammar ) || {};
	return g.lang === lang
		? g
		: FALLBACK[ lang ] || { plural: 'one_other', decimal: ',' };
}

const GLYPHS = [
	[ '⅛', 0.125 ],
	[ '¼', 0.25 ],
	[ '⅓', 1 / 3 ],
	[ '½', 0.5 ],
	[ '⅔', 2 / 3 ],
	[ '¾', 0.75 ],
];

/**
 * The fractions a kitchen can actually measure, for spoons and cups.
 *
 * Scaling lands on values between them — 4 → 5 servings turns ½ tsp into
 * 0.625 — and a decimal spoon is unreadable at the stove, so the amount snaps
 * to the nearest measure that exists instead of printing "0.6 tsp".
 */
const SPOONS = [ 0, 0.125, 0.25, 1 / 3, 0.5, 2 / 3, 0.75, 1 ];

/**
 * Nearest scoopable amount. Above 3 spoons the halves are enough.
 *
 * @param {number} qty Quantity.
 * @return {number} Snapped quantity.
 */
export function snapSpoon( qty ) {
	if ( ! qty || qty <= 0 ) {
		return qty;
	}
	if ( qty >= 3 ) {
		return Math.round( qty * 2 ) / 2;
	}
	const whole = Math.floor( qty + 1e-9 );
	const frac = qty - whole;
	let best = SPOONS[ 0 ];
	for ( const step of SPOONS ) {
		if ( Math.abs( step - frac ) < Math.abs( best - frac ) ) {
			best = step;
		}
	}
	return whole + best;
}

/**
 * "½", "1¼", "230", "1,5" (uk decimal comma).
 *
 * @param {number} qty  Quantity.
 * @param {string} lang uk|en.
 */
export function formatQty( qty, lang ) {
	if ( ! qty || qty <= 0 ) {
		return '';
	}
	// Round big numbers sensibly: 1234 g → 1230 g, 57.6 g → 58 g.
	if ( qty >= 100 ) {
		qty = Math.round( qty / 5 ) * 5;
	} else if ( qty >= 10 ) {
		qty = Math.round( qty );
	}
	const whole = Math.floor( qty + 1e-9 );
	const frac = qty - whole;
	if ( frac < 0.01 ) {
		return String( whole );
	}
	for ( const [ glyph, value ] of GLYPHS ) {
		if ( Math.abs( frac - value ) < 0.04 ) {
			return ( whole || '' ) + glyph;
		}
	}
	const out = String( Math.round( qty * 10 ) / 10 );
	return out.replace( '.', grammar( lang ).decimal );
}

/**
 * Unit form agreeing with the number: 1 пучок, 3 пучки, 5 пучків, ½ склянки.
 *
 * @param {string[]|null} forms [one, few, many] (uk) or [one, other] (en).
 * @param {number}        qty   Quantity.
 * @param {string}        lang  uk|en.
 * @return {string|null} Form, or null when the unit does not inflect.
 */
export function unitFor( forms, qty, lang ) {
	if ( ! forms ) {
		return null;
	}
	const rule = grammar( lang ).plural;
	if ( Math.floor( qty ) !== qty ) {
		// Fractions: Ukrainian genitive singular ("½ склянки"); others plural above one
		// (Brazilian Portuguese: from two).
		if ( rule === 'slavic' ) {
			return forms[ 1 ];
		}
		return ( rule === 'pt' ? qty < 2 : qty <= 1 )
			? forms[ 0 ]
			: forms[ 2 ] || forms[ 1 ];
	}
	if ( rule === 'pt' ) {
		return qty < 2 ? forms[ 0 ] : forms[ 2 ] || forms[ 1 ];
	}
	if ( rule !== 'slavic' ) {
		return qty === 1 ? forms[ 0 ] : forms[ 2 ] || forms[ 1 ];
	}
	const m10 = qty % 10;
	const m100 = qty % 100;
	if ( m10 === 1 && m100 !== 11 ) {
		return forms[ 0 ];
	}
	if ( m10 >= 2 && m10 <= 4 && ( m100 < 12 || m100 > 14 ) ) {
		return forms[ 1 ];
	}
	return forms[ 2 ];
}
